<template>
  <PageChrome>
    <template #title>Usage</template>

    <template #actions>
      <div class="filter-field" style="width: 130px">
        <SearchSelect
          v-model="filters.mode"
          :options="modeOptions"
          placeholder="All types"
          clear-label="All types"
          hideSearch
        />
      </div>

      <div class="filter-field" style="width: 150px">
        <SearchSelect
          v-model="filters.status"
          :options="statusOptions"
          placeholder="All statuses"
          clear-label="All statuses"
          hideSearch
        />
      </div>

      <div class="filter-field" style="width: 220px">
        <SearchSelect
          v-model="filters.account_domain"
          :optionsFunction="searchAccountsByDomain"
          :maxResults="20"
          placeholder="All accounts"
          search-placeholder="Search accounts..."
          clear-label="All accounts"
        />
      </div>

      <div class="filter-field" style="width: 220px">
        <SearchSelect
          v-model="filters.agent_selector"
          :options="agentSelectOptions"
          placeholder="All agents"
          clear-label="All agents"
        />
      </div>

      <div class="filter-field" style="width: 220px">
        <SearchSelect
          v-model="filters.sales_play_id"
          :options="playSelectOptions"
          placeholder="All plays"
          clear-label="All plays"
        />
      </div>

      <div class="filter-field" style="width: 150px">
        <SearchSelect
          v-model="filters.range"
          :options="rangeOptions"
          :allowClear="false"
          hideSearch
        />
      </div>
    </template>

    <div class="dashboard">
      <div v-if="dashboardError" class="error">{{ dashboardError }}</div>
      <div v-else-if="dashboardLoading" class="muted">Loading dashboard…</div>

      <div v-else class="card">
        <div class="card-header">
          <div class="card-title">{{ activeMetric.title }}</div>
          <div class="filter-field" style="width: 170px">
            <SearchSelect
              v-model="metric"
              :options="metricOptions"
              :allowClear="false"
              hideSearch
            />
          </div>
        </div>
        <div class="chart">
          <div v-if="chartPoints.length === 0" class="muted">No data.</div>
          <div v-else class="bars">
            <div v-for="p in chartPoints" :key="p.day" class="bar-wrap" :title="barTitle(p)">
              <div class="bar-area">
                <div class="stack" :style="{ height: barHeight(p) }">
                  <div
                    v-for="series in stackedSeries"
                    :key="series.key"
                    class="seg"
                    :class="series.key"
                    :style="{ height: segHeight(p, series) }"
                  >
                    <div class="seg-label" v-if="segLabelVisible(p, series)">{{ formatValue(seriesValue(p, series)) }}</div>
                  </div>
                </div>
              </div>
              <div class="bar-label">{{ shortDay(p.day) }}</div>
            </div>
          </div>
        </div>
        <div class="legend" v-if="legendItems.length">
          <div v-for="item in legendItems" :key="item.key" class="legend-item">
            <span class="swatch" :class="item.key"></span>{{ item.label }}
          </div>
        </div>
        <div class="muted" style="font-size:12px; margin-top: 8px;">Total: {{ formatValue(chartTotal) }}</div>
      </div>
    </div>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="list-panel">
      <div class="list-panel-header">
        <div class="list-panel-title">Agent Runs <span class="muted">({{ runs.length }}{{ nextOffset ? '+' : '' }})</span></div>
      </div>

      <div v-if="loading && runs.length === 0" class="list-panel-empty">Loading…</div>

      <AgentRunsTable
        v-else
        :runs="runs"
        :nextOffset="nextOffset"
        :loading="loading"
        :showBudgets="true"
        :displayTz="DISPLAY_TZ"
        @openRun="openRunPage"
        @loadMore="loadRuns(false)"
      />
    </div>

  </PageChrome>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AgentRunsTable from '../../components/AgentRunsTable.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { apiFetch } from '../../services/api.ts'
import { formatAgentOptions } from '../../services/agentOptions.ts'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

const DISPLAY_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const runs = ref([])
const loading = ref(false)
const error = ref('')
const nextOffset = ref(null)

const dashboardLoading = ref(false)
const dashboardError = ref('')
const costPoints = ref([])
const callsPoints = ref([])

const agents = ref([])
const plays = ref([])

const filters = ref({
  mode: '',
  status: '',
  account_domain: '',
  agent_selector: '',
  sales_play_id: '',
  range: '7',
})

const modeOptions = [
  { value: 'agent', label: 'Agent' },
  { value: 'source', label: 'Find' },
  { value: 'filter', label: 'Qualify' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'sequence', label: 'Sequence' },
  { value: 'revision', label: 'Revision' },
  { value: 'chat', label: 'Chat' },
  { value: 'enrichment', label: 'Enrichment' },
  { value: 'flock_strategy', label: 'Strategy agent' },
  { value: 'flock_play', label: 'Play' },
]

const statusOptions = [
  { value: 'queued', label: 'queued' },
  { value: 'running', label: 'running' },
  { value: 'succeeded', label: 'succeeded' },
  { value: 'failed', label: 'failed' },
  { value: 'stopped', label: 'stopped' },
]

const rangeOptions = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
]

// Series are stacked bottom-up in declaration order.
const METRICS = {
  uc: {
    title: 'UC costs by day',
    source: 'cost',
    format: (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    series: [
      { key: 'openai', label: 'OpenAI', field: 'openai_cost_uc' },
      { key: 'anthropic', label: 'Claude', field: 'anthropic_cost_uc' },
      { key: 'leadiq', label: 'LeadIQ', field: 'leadiq_cost_uc' },
    ],
  },
  usd: {
    title: 'LLM cost by day',
    source: 'cost',
    format: (n) => `$${formatUsd(n)}`,
    series: [
      { key: 'openai', label: 'OpenAI', field: 'openai_cost_usd' },
      { key: 'anthropic', label: 'Claude', field: 'anthropic_cost_usd' },
      { key: 'exa', label: 'Exa.ai', field: 'exa_cost_usd' },
    ],
  },
  calls: {
    title: 'API calls by day',
    source: 'calls',
    format: (n) => fmtInt(n),
    series: [
      { key: 'openai', label: 'OpenAI', field: 'openai_call_count' },
      { key: 'anthropic', label: 'Anthropic', field: 'anthropic_call_count' },
      { key: 'salesforce', label: 'Salesforce', field: 'salesforce_call_count' },
      { key: 'leadiq', label: 'LeadIQ', field: 'leadiq_call_count' },
    ],
  },
}

const metricOptions = [
  { value: 'uc', label: 'UC costs' },
  { value: 'usd', label: 'LLM costs' },
  { value: 'calls', label: 'API calls' },
]

const metric = ref('uc')
const activeMetric = computed(() => METRICS[metric.value])
const stackedSeries = computed(() => [...activeMetric.value.series].reverse())
const chartPoints = computed(() => (activeMetric.value.source === 'cost' ? costPoints.value : callsPoints.value))

const router = useRouter()

function statusClass(status) {
  if (status === 'succeeded') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'running') return 'warning'
  if (status === 'queued') return 'warning'
  return ''
}

function formatTs(ts) {
  try {
    return new Date(ts).toLocaleString(undefined, { timeZone: DISPLAY_TZ })
  } catch {
    return ts
  }
}

function buildQuery(offset) {
  const params = new URLSearchParams()
  params.set('limit', '100')
  params.set('offset', String(offset || 0))
  params.set('include_test', 'true')
  params.set('execution_mode', String(filters.value.mode || 'all'))
  if (filters.value.status) params.set('status', filters.value.status)
  if (filters.value.account_domain) params.set('account_domain', filters.value.account_domain)
  if (filters.value.sales_play_id) params.set('sales_play_id', filters.value.sales_play_id)

  const sel = String(filters.value.agent_selector || '')
  if (sel.startsWith('id:')) params.set('agent_id', sel.slice(3))

  const { start_date, end_date } = computeDateRange()
  if (start_date) params.set('start_date', start_date)
  if (end_date) params.set('end_date', end_date)
  return params.toString()
}

function buildDashboardQuery() {
  const params = new URLSearchParams()
  params.set('execution_mode', String(filters.value.mode || 'all'))
  if (filters.value.account_domain) params.set('account_domain', filters.value.account_domain)
  if (filters.value.sales_play_id) params.set('sales_play_id', filters.value.sales_play_id)

  const sel = String(filters.value.agent_selector || '')
  if (sel.startsWith('id:')) params.set('agent_id', sel.slice(3))

  const { start_date, end_date } = computeDateRange()
  if (start_date) params.set('start_date', start_date)
  if (end_date) params.set('end_date', end_date)
  return params.toString()
}

function ymdInTz(d) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const y = parts.find(p => p.type === 'year')?.value
  const m = parts.find(p => p.type === 'month')?.value
  const day = parts.find(p => p.type === 'day')?.value
  return `${y}-${m}-${day}`
}

function computeDateRange() {
  const days = Number(filters.value.range || 7)
  const end = new Date()
  const end_ymd = ymdInTz(end)
  const [y, m, d] = end_ymd.split('-').map(n => Number(n))
  const base = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0))
  base.setUTCDate(base.getUTCDate() - Math.max(0, days - 1))
  const start_ymd = ymdInTz(base)
  return { start_date: start_ymd, end_date: end_ymd }
}

async function loadDashboard() {
  dashboardLoading.value = true
  dashboardError.value = ''
  try {
    const q = buildDashboardQuery()
    const [costResp, callsResp] = await Promise.all([
      apiFetch(`/monitoring/openai_cost_by_day?${q}`),
      apiFetch(`/monitoring/api_calls_by_day?${q}`),
    ])
    costPoints.value = Array.isArray(costResp?.points) ? costResp.points : []
    callsPoints.value = Array.isArray(callsResp?.points) ? callsResp.points : []
  } catch (e) {
    dashboardError.value = e?.message || 'Failed to load dashboard'
    costPoints.value = []
    callsPoints.value = []
  } finally {
    dashboardLoading.value = false
  }
}

async function applyFilters() {
  await Promise.all([loadRuns(true), loadDashboard()])
}

function formatDay(ts) {
  try {
    return new Date(ts).toLocaleDateString(undefined, { timeZone: DISPLAY_TZ })
  } catch {
    return String(ts)
  }
}

function shortDay(ts) {
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      timeZone: DISPLAY_TZ,
      month: 'numeric',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

function formatUsd(n) {
  const x = Number(n || 0)
  return x.toFixed(2)
}

async function searchAccountsByDomain(query) {
  const qs = new URLSearchParams({ q: query, limit: '20' })
  const resp = await apiFetch(`/accounts/paginated?${qs}`)
  const accts = Array.isArray(resp?.items) ? resp.items : []
  return accts.map((a) => ({
    value: String(a.domain || ''),
    label: String(a.name || a.domain || '').trim() || String(a.domain || ''),
    domain: String(a.domain || ''),
  }))
}

const agentSelectOptions = computed(() => {
  const items = formatAgentOptions(agents.value, { useIdPrefix: true })
    .map(a => ({ value: String(a.value || ''), label: String(a.label || '') }))
    .filter(o => o.value)
  return items
})

const playSelectOptions = computed(() => {
  return plays.value
    .map((p) => ({ value: String(p.id || ''), label: String(p.name || '').trim() || String(p.id || '') }))
    .filter(o => o.value)
})

async function loadFilterOptions() {
  try {
    const [agentResp, playResp] = await Promise.all([
      apiFetch('/agents?include_sales_play_owned=true'),
      apiFetch('/sales-plays'),
    ])
    agents.value = Array.isArray(agentResp?.agents) ? agentResp.agents : []
    plays.value = Array.isArray(playResp?.plays) ? playResp.plays : []
  } catch {
    agents.value = []
    plays.value = []
  }
}

function fmtInt(n) {
  const x = Number(n || 0)
  if (!Number.isFinite(x)) return '0'
  return String(Math.trunc(x))
}

function seriesValue(p, series) {
  return Number(p?.[series.field] || 0)
}

function dayTotal(p) {
  let t = 0
  for (const series of activeMetric.value.series) t += seriesValue(p, series)
  return t
}

function maxDayTotal() {
  let m = 0
  for (const p of chartPoints.value) {
    const v = dayTotal(p)
    if (v > m) m = v
  }
  return m
}

const chartTotal = computed(() => {
  let t = 0
  for (const p of chartPoints.value) t += dayTotal(p)
  return t
})

function formatValue(n) {
  return activeMetric.value.format(n)
}

function barHeight(p) {
  const max = maxDayTotal()
  if (!max) return '0%'
  const pct = Math.max(0, Math.min(100, (dayTotal(p) / max) * 100))
  return `${pct}%`
}

function segHeight(p, series) {
  const total = dayTotal(p)
  if (!total) return '0%'
  const pct = Math.max(0, Math.min(100, (seriesValue(p, series) / total) * 100))
  return `${pct}%`
}

function segLabelVisible(p, series) {
  const total = dayTotal(p)
  const max = maxDayTotal()
  if (!total || !max) return false
  const v = seriesValue(p, series)
  if (!v) return false
  const overallPct = Math.max(0, Math.min(100, (total / max) * 100))
  const segPct = Math.max(0, Math.min(100, (v / total) * 100))
  return (overallPct * segPct) / 100 >= 12
}

function barTitle(p) {
  const parts = activeMetric.value.series.map(s => `${s.label} ${activeMetric.value.format(seriesValue(p, s))}`)
  return `${formatDay(p?.day)}: ${parts.join(', ')}`
}

const legendItems = computed(() => {
  return activeMetric.value.series.filter(series => {
    for (const p of chartPoints.value) {
      if (seriesValue(p, series) > 0) return true
    }
    return false
  })
})

async function loadRuns(reset = false) {
  loading.value = true
  error.value = ''
  try {
    const off = reset ? 0 : Number(nextOffset.value || 0)
    if (!reset && !nextOffset.value) return

    const resp = await apiFetch(`/agent_runs?${buildQuery(off)}`)
    const newRuns = resp?.runs || []
    nextOffset.value = resp?.next_offset ?? null

    if (reset) {
      runs.value = newRuns
    } else {
      runs.value = [...runs.value, ...newRuns]
    }
  } catch (e) {
    error.value = e?.message || 'Failed to load runs'
  } finally {
    loading.value = false
  }
}

function openRunPage(r) {
  const id = r?.id
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

watch(filters, () => { applyFilters() }, { deep: true })

onMounted(async () => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  await loadFilterOptions()
  await Promise.all([loadRuns(true), loadDashboard()])
})

onBeforeUnmount(() => { clearScope() })
</script>

<style scoped>
.error { color: var(--danger); margin-bottom: 10px; }
.muted { color: var(--muted); }

.badge.test {
  background: rgba(124, 58, 237, 0.10);
  border-color: rgba(124, 58, 237, 0.25);
  color: #7c3aed;
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 12px;
}


.filters {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  color: var(--table-header-text);
  font-weight: 600;
}

.run-row { cursor: pointer; }
.run-row:hover { background: var(--row-hover-bg); }

.run-detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 10px;
}

.detail-box {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  background: var(--table-header-bg);
}

.detail-pre {
  margin: 0;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  background: var(--md-pre-bg);
  color: var(--md-pre-text);
  overflow: auto;
}

.dashboard {
  margin-bottom: 12px;
}

.card {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 12px;
  padding: 14px;
}

.card-title {
  font-weight: 700;
  color: var(--text);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.chart {
  height: 160px;
  display: flex;
  align-items: stretch;
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
  height: 100%;
}

.bar-wrap {
  flex: 1;
  min-width: 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  gap: 6px;
  height: 100%;
}

.bar-area {
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: stretch;
  min-height: 0;
  position: relative;
}

.value-label {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: var(--muted);
  white-space: nowrap;
}

.bar {
  background: var(--chart-openai);
  border-radius: 6px 6px 0 0;
  width: 100%;
  position: relative;
}

.bar-value {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: var(--chart-seg-text);
  opacity: 0.9;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
  white-space: nowrap;
  pointer-events: none;
}

.bar-label {
  font-size: 10px;
  color: var(--muted);
  text-align: center;
}

.usage-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.outputs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.sep {
  opacity: 0.7;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 12px;
  background: var(--surface);
}

.stack {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: 6px 6px 0 0;
  overflow: hidden;
  width: 100%;
}

.seg {
  width: 100%;
  position: relative;
}

.seg-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  color: var(--chart-seg-text);
  opacity: 0.95;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}
.seg.openai { background: var(--chart-openai); }
.seg.anthropic { background: var(--chart-anthropic); }
.seg.salesforce { background: var(--chart-salesforce); }
.seg.leadiq { background: var(--chart-leadiq); }
.seg.exa { background: var(--chart-exa); }

.legend {
  margin-top: 10px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--muted);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}

.swatch.openai { background: var(--chart-openai); }
.swatch.anthropic { background: var(--chart-anthropic); }
.swatch.salesforce { background: var(--chart-salesforce); }
.swatch.leadiq { background: var(--chart-leadiq); }
.swatch.exa { background: var(--chart-exa); }
</style>
